import {
  Badge,
  BodyText,
  Box,
  Flex,
  Heading,
  Modal,
  ModalContent,
  ModalRoot,
  ModalTrigger,
  Tab,
  Tabs,
  TabsList,
  ToggleGroup,
  UnstyledIconButton,
  ValidationText,
} from '@utilitywarehouse/hearth-react';
import { InfoMediumIcon } from '@utilitywarehouse/hearth-react-icons';
import { FormikProps, useFormikContext } from 'formik';
import { FC, useEffect } from 'react';

import { DISCOUNT_IDS } from '@constants/apiConstants';
import { ProductSubtype } from '@generated/gql/graphql';
import { useFieldProps } from '@hooks/useFieldProps';
import { useSyncState } from '@hooks/useSyncState';
import { BroadbandFormValues, BroadbandQuote } from '@src/types/broadband';
import { BundleBasketData } from '@src/types/bundle';
import { useTrackingContext } from '@tracking/Provider/Context';
import {
  SignupBroadbandTariffsDisplayedEventContext,
  useTrackSignupBroadbandTariffsDisplayedOnce,
} from '@tracking/trackingEvents';

import { Tariff } from '../Tariff';
import { PlanTypes } from '../types';
import { shouldShowDigitalHomePhone } from '../utils/shouldShowDigitalHomePhone';
import {
  getPageTitle,
  getPlanDescription,
  planTooltipContent,
  sectionTitles,
} from './planContents';
import { sortTariffsBySpeed } from './utils';

interface PlanSelectionProps {
  basket: BundleBasketData;
}

const broadbandTariffsEventFormatter = (
  quotes: BroadbandQuote[]
): SignupBroadbandTariffsDisplayedEventContext => ({
  tariffs_displayed: quotes.map(({ product }) => product.name),
});

interface GroupedTariff {
  type: PlanTypes;
  tariffs: BroadbandQuote[];
}

const getTechType = (subtype: ProductSubtype): PlanTypes => {
  if (subtype === ProductSubtype.BroadbandFttp) {
    return PlanTypes.FTTP;
  }

  if (subtype === ProductSubtype.BroadbandFttc) {
    return PlanTypes.FTTC;
  }

  return PlanTypes.Copper;
};

const getGroupedTariffs = (quotes: BroadbandQuote[]) => {
  const groups = quotes
    .reduce((grouped: GroupedTariff[], quote: BroadbandQuote) => {
      const techType = getTechType(quote.product.subtype);
      const existingGroup = grouped.find((g) => g.type === techType);

      if (!existingGroup) {
        return [
          ...grouped,
          {
            type: techType,
            tariffs: [quote],
          },
        ];
      }

      return [
        ...grouped.map((g) => {
          if (g.type === techType) {
            return {
              ...g,
              tariffs: [...g.tariffs, quote],
            };
          }
          return g;
        }),
      ];
    }, [])
    .map((group) => ({
      ...group,
      tariffs: sortTariffsBySpeed(group.tariffs),
    }));

  // Sort groups to ensure Full Fibre appears as tab #1
  return groups.sort((a, b) => {
    if (a.type === PlanTypes.FTTP) return -1;
    if (b.type === PlanTypes.FTTP) return 1;
    return 0;
  });
};

const PlanSelection: FC<PlanSelectionProps> = ({ basket }) => {
  const trackTariffsDisplayedOnce =
    useTrackSignupBroadbandTariffsDisplayedOnce();
  const { trackingContexts } = useTrackingContext();
  const groupedTariffs: GroupedTariff[] = getGroupedTariffs(
    basket.broadband.quotes
  );

  const [selectedSubtype, setSelectedSubtype] = useSyncState(
    'bb-subtype',
    groupedTariffs.find((group) => group.type === PlanTypes.FTTP)?.type ??
      groupedTariffs[0].type
  );

  const [tooltipOpen, setTooltipOpen] = useSyncState('bb-tooltip', false);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    submitCount,
    setFieldValue,
  }: FormikProps<BroadbandFormValues> = useFormikContext();

  const getFieldProps = useFieldProps<BroadbandFormValues>({
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    submitCount,
  });

  useEffect(() => {
    trackTariffsDisplayedOnce(
      broadbandTariffsEventFormatter(basket.broadband.quotes),
      {
        contexts: trackingContexts,
      }
    );
  }, [basket.broadband.quotes, trackingContexts, trackTariffsDisplayedOnce]);

  const hasTabs = groupedTariffs.length > 1;
  const selectedGroup = groupedTariffs?.find(
    (group) => group.type === selectedSubtype
  );
  const selectedGroupTariffs = selectedGroup?.tariffs || [];

  return (
    <ModalRoot
      open={tooltipOpen}
      onOpenChange={(open: boolean) => setTooltipOpen(open)}
    >
      <Flex direction="column" spacing="xl" asChild>
        <section>
          <header className="wrapper">
            <Flex
              display="inline-flex"
              alignItems="center"
              spacing="sm"
              asChild
            >
              <Heading size="lg" data-testid="broadband-heading">
                {getPageTitle(hasTabs, selectedSubtype)}{' '}
                {groupedTariffs.length === 1 && (
                  <ModalTrigger>
                    <UnstyledIconButton
                      label={selectedSubtype}
                      onClick={() => setTooltipOpen(true)}
                    >
                      <InfoMediumIcon />
                    </UnstyledIconButton>
                  </ModalTrigger>
                )}
              </Heading>
            </Flex>
            {hasTabs && (
              <Tabs
                value={selectedSubtype}
                onValueChange={(val) => setSelectedSubtype(val as PlanTypes)}
              >
                <TabsList>
                  {groupedTariffs.map((subtype) => (
                    <Tab key={subtype.type} value={subtype.type}>
                      {subtype.type}
                    </Tab>
                  ))}
                </TabsList>
              </Tabs>
            )}
          </header>
          <div className="wrapper">
            {hasTabs && (
              <Flex
                display="inline-flex"
                alignItems="center"
                spacing="sm"
                asChild
              >
                <Heading size="md" className="broadband__PlanTitle">
                  {sectionTitles[selectedSubtype]}{' '}
                  <ModalTrigger>
                    <UnstyledIconButton
                      label={selectedSubtype}
                      onClick={() => setTooltipOpen(true)}
                    >
                      <InfoMediumIcon />
                    </UnstyledIconButton>
                  </ModalTrigger>
                  {selectedGroupTariffs?.some((quote) =>
                    quote.discounts.some(
                      (discount) =>
                        discount.id ===
                          DISCOUNT_IDS.TBYB_BROADBAND_LINE_DISCOUNT &&
                        discount.qualified
                    )
                  ) && (
                    <Badge
                      data-testid="discount-badge"
                      variant="subtle"
                      colorScheme="broadband"
                    >
                      Limited Time Offers
                    </Badge>
                  )}
                </Heading>
              </Flex>
            )}
            {selectedGroupTariffs?.length > 0 && (
              <Flex columnGap="50" marginY="200" asChild>
                <BodyText>
                  {getPlanDescription(
                    shouldShowDigitalHomePhone(selectedGroupTariffs[0]),
                    selectedSubtype
                  )}
                </BodyText>
              </Flex>
            )}
            <ToggleGroup
              type="single"
              aria-label="Choose your broadband plan"
              className="broadband__PlanSelection"
              {...getFieldProps('selectedTariff')}
              id="selectedTariff"
              onValueChange={(value: string) => {
                if (value) {
                  setFieldValue('selectedTariff', value);
                }
              }}
              spacing="xl"
            >
              {groupedTariffs
                ?.find((group) => group.type === selectedSubtype)
                ?.tariffs.map((quote) => (
                  <div key={quote.id} className="broadband__PlanWrapper">
                    <Tariff
                      quote={quote}
                      value={quote.id}
                      selected={values.selectedTariff === quote.id}
                    />
                  </div>
                ))}
            </ToggleGroup>
            {errors.selectedTariff && touched.selectedTariff && (
              <Box marginTop="200">
                <ValidationText status="invalid">
                  Please choose a broadband plan
                </ValidationText>
              </Box>
            )}
          </div>
        </section>
      </Flex>
      <Modal loading={false} heading={selectedSubtype} hideCloseButton={false}>
        <ModalContent>{planTooltipContent[selectedSubtype]}</ModalContent>
      </Modal>
    </ModalRoot>
  );
};

export { PlanSelection };
